import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { Card, CardContent } from '@repo/ui/card'
import { getAdminCatalogOverview } from '@repo/sdk/gen'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
	Archive,
	Box,
	ChevronRight,
	CircleAlert,
	Layers3,
	Search
} from 'lucide-react'
import { BackofficeShell } from '@/components/backoffice-shell'
import { QueryErrorState } from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'

export const Route = createFileRoute('/')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	component: BackofficeHome
})

function BackofficeHome() {
	const { t } = useTranslation()
	const overviewQuery = useQuery({
		queryKey: ['catalog', 'overview'],
		queryFn: () =>
			getAdminCatalogOverview().then((response) => response.data.data)
	})
	const overview = overviewQuery.data

	return (
		<BackofficeShell contentClassName='max-w-7xl'>
			<header className='mb-10 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between'>
				<div>
					<p className='mb-2 font-medium text-primary text-sm'>
						{t('common:dashboard')}
					</p>
					<h1 className='font-semibold text-3xl tracking-tight md:text-4xl'>
						{t('common:welcome')}
					</h1>
					<p className='mt-3 max-w-2xl text-muted-foreground'>
						{t('common:overview_copy')}
					</p>
				</div>
				<Button nativeButton={false} render={<Link to='/cards' />}>
					<Search />
					{t('common:search')}
				</Button>
			</header>
			<section className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
				<Metric
					accent
					icon={CircleAlert}
					label={t('common:needs_catalog')}
					value={formatMetric(
						overview?.needsCatalog,
						overviewQuery.isPending
					)}
				/>
				<Metric
					icon={Box}
					label={t('common:active_cards')}
					value={formatMetric(
						overview?.activeCards,
						overviewQuery.isPending
					)}
				/>
				<Metric
					icon={Layers3}
					label={t('common:active_expansions')}
					value={formatMetric(
						overview?.activeExpansions,
						overviewQuery.isPending
					)}
				/>
				<Metric
					icon={Archive}
					label={t('common:printings')}
					value={formatMetric(
						overview?.printings,
						overviewQuery.isPending
					)}
				/>
			</section>
			<section className='mt-8 grid gap-6 xl:grid-cols-[1.45fr_0.9fr]'>
				<Card className='overflow-hidden rounded-2xl'>
					<div className='flex items-center justify-between border-b p-6'>
						<div>
							<h2 className='font-semibold'>
								{t('common:recent_attention')}
							</h2>
							<p className='mt-1 text-muted-foreground text-sm'>
								{t('common:provider_product')}
							</p>
						</div>
						<Button
							nativeButton={false}
							render={<Link to='/queue' />}
							size='sm'
							variant='outline'
						>
							{t('common:open_queue')}
							<ChevronRight />
						</Button>
					</div>
					<CardContent className='p-0'>
						{overviewQuery.isError ? (
							<QueryErrorState
								message={t('common:overview_load_error')}
								onRetry={() => overviewQuery.refetch()}
							/>
						) : (
							<p className='p-6 text-muted-foreground text-sm'>
								{t('common:overview_queue_copy')}
							</p>
						)}
					</CardContent>
				</Card>
				<Card className='rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground'>
					<CardContent className='p-6'>
						<Badge
							className='border-white/20 bg-white/10 text-white'
							variant='outline'
						>
							{t('common:suggestion')}
						</Badge>
						<h2 className='mt-6 font-semibold text-2xl tracking-tight'>
							{formatMetric(
								overview?.needsCatalog,
								overviewQuery.isPending
							)}
						</h2>
						<p className='mt-2 text-primary-foreground/75 text-sm'>
							{t('common:overview_queue_copy')}
						</p>
						<div className='mt-8 rounded-xl bg-black/10 p-4'>
							<p className='text-primary-foreground/60 text-xs uppercase tracking-wider'>
								Candidate printing
							</p>
							<p className='mt-1 font-medium'>
								{t('common:needs_catalog')}
							</p>
						</div>
						<Button
							className='mt-6 bg-white text-primary hover:bg-white/90'
							nativeButton={false}
							render={<Link to='/queue' />}
						>
							{t('common:review_match')}
							<ChevronRight />
						</Button>
					</CardContent>
				</Card>
			</section>
		</BackofficeShell>
	)
}

function Metric(props: {
	icon: typeof Box
	label: string
	value: string
	accent?: boolean
}) {
	const Icon = props.icon
	return (
		<Card className='rounded-2xl'>
			<CardContent className='p-5'>
				<div
					className={`mb-7 grid size-10 place-items-center rounded-xl ${props.accent ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'bg-primary/10 text-primary'}`}
				>
					<Icon className='size-5' />
				</div>
				<p className='font-semibold text-2xl tracking-tight'>
					{props.value}
				</p>
				<p className='mt-1 text-muted-foreground text-sm'>
					{props.label}
				</p>
			</CardContent>
		</Card>
	)
}

function formatMetric(value: number | undefined, isPending: boolean) {
	if (value !== undefined) {
		return String(value)
	}
	return isPending ? '…' : '—'
}
